import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AvatarImage } from "@/components/ui/avatar";
// Using react-icons for brand-colored social media icons
import { SiLinkedin, SiInstagram, SiFacebook } from "react-icons/si";
import type { TeamMember } from "@/data/team";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

function getAvatarUrl(member: TeamMember) {
  if (member.image) return member.image;
  if (member.linkedin) return `https://unavatar.io/${encodeURI(member.linkedin)}`;
  return undefined;
}

export function TeamGrid({ team, title, className = "", showBio = true, interactive = true }: { team: TeamMember[]; title?: string; className?: string; showBio?: boolean; interactive?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expandedBios, setExpandedBios] = useState<Record<string, boolean>>({});

  const toggleBio = (memberName: string) => {
    setExpandedBios(prev => ({
      ...prev,
      [memberName]: !prev[memberName]
    }));
  };

  const truncateText = (text: string, maxLength: number = 280) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const handleCardClick = (member: TeamMember) => {
    if (!interactive) return;
    const anchor = member.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    navigate(`/about#${anchor}`);
  };
  return (
    <section className={`w-full ${className}`}>
      {title && (
        <h2 className="text-3xl font-bold text-center mb-8">{title}</h2>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {team.map((member, idx) => (
          <Card
            key={member.name}
            className={`glass p-6 flex flex-col items-center text-center animate-scale-in transition-all duration-200 ${
              interactive 
                ? 'cursor-pointer hover:shadow-md hover:scale-[1.02]' 
                : 'cursor-default'
            }`}
            style={{ animationDelay: `${idx * 100}ms` }}
            onClick={() => handleCardClick(member)}
          >
            <Avatar className="h-20 w-20 sm:h-24 sm:w-24 mb-4 bg-primary/10 text-primary overflow-hidden rounded-full">
              <AvatarImage
                src={getAvatarUrl(member)}
                alt={`${member.name} profile photo`}
                className="object-cover"
                loading="lazy"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  target.src = "";
                }}
              />
              <AvatarFallback className="font-semibold text-lg sm:text-xl">{member.initials}</AvatarFallback>
            </Avatar>
            <h3 className="text-lg font-semibold">{member.name}</h3>
            {member.role && (
              <p className="text-sm text-muted-foreground mt-1">{member.role}</p>
            )}
            {(member.linkedin || member.instagram || member.facebook) && (
              <div className="mt-3">
                <div className="flex items-center justify-center gap-2">
                  {member.linkedin && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`LinkedIn profile of ${member.name}`}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition"
                      title="LinkedIn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SiLinkedin color="#0A66C2" size={20} aria-hidden="true" />
                    </a>
                  )}
                  {member.instagram && (
                    <a
                      href={member.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Instagram profile of ${member.name}`}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition"
                      title="Instagram"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SiInstagram color="#E4405F" size={20} aria-hidden="true" />
                    </a>
                  )}
                  {member.facebook && (
                    <a
                      href={member.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Facebook profile of ${member.name}`}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition"
                      title="Facebook"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SiFacebook color="#1877F2" size={20} aria-hidden="true" />
                    </a>
                  )}
                </div>
              </div>
            )}
            {showBio && (
              (() => {
                const text = member.bioKey ? t(member.bioKey) : member.bio;
                if (!text) return null;
                
                const isExpanded = expandedBios[member.name];
                const shouldTruncate = text.length > 280;
                const displayText = shouldTruncate && !isExpanded ? truncateText(text) : text;
                
                return (
                  <div className="text-sm text-muted-foreground mt-3">
                    <p>
                      {displayText.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                          {line}
                          {index < displayText.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </p>
                    {shouldTruncate && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBio(member.name);
                        }}
                        className="text-primary hover:text-primary/80 text-xs mt-1 underline transition-colors"
                        aria-label={isExpanded ? t('common.readLess') : t('common.readMore')}
                      >
                        {isExpanded ? t('common.readLess') : t('common.readMore')}
                      </button>
                    )}
                  </div>
                );
              })()
            )}
          </Card>
        ))}
      </div>
    </section>
  );
}
